import { EmailValue } from "./EmailValue";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { Divider, Row, Col, Tag, Popover } from "antd";
import { dayjs } from "Config/dayjs";
import { FileAttach } from "Components/General/FileAttach";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { fileUploadStore } from "Pages/Project/Documents/FileUpload/FileUploadStore";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { extractIdFromUId } from "../utils/extractIdFromUId";
import { isEmpty } from "lodash";

export interface EmailDetailsBodyProps extends WithTranslation {
  to: string[];
  from: string;
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  timestamp: number;
  id: string;
  type: API_MAILS_TYPE | "DRAFT";
  projectSpecific: boolean;
  attachments: API_FileAttachment[];
  correspondents: API_MailResponse["correspondents"];
  abstractRecipients?: API_AbstractRecipients;
  moveAttachToProject: (filesMap: { [id: string]: string }, folderId: number) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

export const EmailDetailsBody = withTranslation(I18N_NS.PROJECT)(
  observer(
    ({
      t,
      id,
      type,
      projectSpecific,
      from,
      subject,
      timestamp,
      attachments,
      onRefresh,
      abstractRecipients,
      body,
      correspondents,
      ...rest
    }: EmailDetailsBodyProps) => {
      const ABSTRACT_VIEW = !isEmpty(abstractRecipients);
      const renderSeparator = () => <span style={{ marginRight: 6 }}>,</span>;

      // When using abstractRecipients, we can get a real email instead of the pattern "@user12"
      // so we need to render it as a string with mail value
      // Will be a separate function in the future when only the abstract remains
      const renderValidUserContact = (value: string, correspondent: API_MailAndName): API_MailAndName => {
        const isDirectEmailAddress = !value.startsWith("@");
        return isDirectEmailAddress ? { email: value } : correspondent;
      };

      const renderEmailsList = (list: string[]) => {
        if (list) {
          return list.map((item, idx) => {
            const activeBackupName = renderValidUserContact(item, correspondents[item]);
            const isNotLast = list.length !== idx + 1;
            return (
              <span key={item} className="email-address-item">
                <EmailValue uid={item} backupName={activeBackupName} />
                {isNotLast && renderSeparator()}
              </span>
            );
          });
        } else {
          return null;
        }
      };

      const renderGroupsList = (groups: API_AbstractGroups[]) => {
        const groupNotExistName = t("Non-existent group");
        const userNotExistName = t("Non-existent user");
        return groups.map((group, idx) => {
          const abstractGroupMembers = group.members;
          const abstractGroupId = extractIdFromUId(group.name).value;
          const existedGroup = groupListStore.forMailAndPlot.find((v) => v.id === abstractGroupId);
          const isNotLast = groups.length !== idx + 1;
          const content = (members: string[]) => {
            return members.map((item, idx) => {
              const contact = renderValidUserContact(item, correspondents[item]);
              return (
                <Tag key={correspondents[item]?.name + idx}>{contact?.name || contact?.email || userNotExistName}</Tag>
              );
            });
          };
          return (
            <div key={group.name + idx}>
              <Popover content={content(abstractGroupMembers)} placement="bottom" trigger="hover">
                <FontAwesomeIcon icon="users" fixedWidth />
                <span style={{ marginLeft: 6 }}>{existedGroup?.name || groupNotExistName}</span>
              </Popover>
              {isNotLast && renderSeparator()}
            </div>
          );
        });
      };

      const renderRecipients = (from: API_AbstractMailRecipientEntry) => {
        const { groups, individuals } = from;
        const hasGroups = groups.length > 0;
        const hasIndividuals = individuals.length > 0;
        const showMidSeparator = hasGroups && hasIndividuals;

        return (
          <div className="flex flexWrap">
            {hasGroups && renderGroupsList(groups)}
            {showMidSeparator && renderSeparator()}
            {hasIndividuals && renderEmailsList(individuals)}
          </div>
        );
      };

      const renderListMethod = (key: "to" | "cc" | "bcc") => {
        return ABSTRACT_VIEW ? renderRecipients(abstractRecipients[key]) : renderEmailsList(rest[key]);
      };

      return (
        <div>
          <h2>{subject}</h2>
          <Row justify="space-between">
            <Col>
              <div className="flex flexWrap mb-2">
                <b className="mr-1">{t("From")}:</b>
                {from && <EmailValue uid={from} backupName={correspondents[from]} />}
              </div>
              <div className="flex flexWrap mb-2">
                <b className="mr-1">{t("To")}:</b>
                {renderListMethod("to")}
              </div>
              {rest.cc && (
                <div className="flex flexWrap mb-2">
                  <b className="mr-1">Cc:</b>
                  {renderListMethod("cc")}
                </div>
              )}
              {rest.bcc && (
                <div className="flex flexWrap mb-2">
                  <b className="mr-1">Bcc:</b>
                  {renderListMethod("bcc")}
                </div>
              )}
            </Col>
            <Col>
              <p style={{ opacity: 0.5 }}>
                <b>{dayjs(timestamp).format("dddd, lll")}</b>
              </p>
            </Col>
          </Row>
          {attachments && (
            <ModalConsumer>
              {() =>
                attachments.map((attach) => (
                  <FileAttach
                    key={attach.id}
                    onMove={() =>
                      fileUploadStore.initModal({
                        module: "MAIL",
                        file: attach,
                        mailId: id,
                        mailProjectSpecific: projectSpecific,
                        mailType: type,
                        onSuccess: () => onRefresh?.(),
                      })
                    }
                    attach={attach}
                  />
                ))
              }
            </ModalConsumer>
          )}
          <Divider style={{ height: 2 }} />
          <div className="email-details" dangerouslySetInnerHTML={{ __html: body }} />
        </div>
      );
    }
  )
);
